import React, { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Plus, Edit, MessageCircle, Users as UsersIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { validateOperatingHours } from "@/utils/operatingHours";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import StaffCodeModal from "@/components/StaffCodeModal";
import TimelineView from "@/components/TimelineView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardNew = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, reservation: null });
  const [notesModal, setNotesModal] = useState({ open: false, notes: "" });
  
  // Staff Code Modal state
  const [staffCodeModal, setStaffCodeModal] = useState({
    open: false,
    actionType: "",
    actionDescription: "",
    reservationRef: null,
    onConfirm: null
  });
  
  // Form states
  const [formData, setFormData] = useState({
    customer_name: "",
    phone: "",
    email: "",
    party_size: 2,
    date: format(new Date(), "yyyy-MM-dd"),
    time: "19:00",
    notes: ""
  });

  useEffect(() => {
    fetchReservations();
  }, [selectedDate]);

  const fetchReservations = async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const response = await axios.get(`${API}/reservations?date=${dateStr}`);
      setReservations(response.data);
    } catch (error) {
      console.error("Error fetching reservations:", error);
      toast.error("Failed to fetch reservations");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (booking, newStatus) => {
    // Open staff code modal for confirmation
    setStaffCodeModal({
      open: true,
      actionType: "Status Change",
      actionDescription: `Change status to "${newStatus}" for ${booking.customer_name}`,
      reservationRef: booking.booking_id,
      onConfirm: () => updateStatusConfirmed(booking.booking_id, newStatus)
    });
  };

  const updateStatusConfirmed = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/reservations/${bookingId}/status`, { status: newStatus });
      toast.success("Status updated");
      fetchReservations();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const createReservation = async () => {
    // Validate operating hours
    const validation = validateOperatingHours(formData.date, formData.time);
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    try {
      const bookingRef = `W${Date.now().toString().slice(-6)}`;
      await axios.post(`${API}/webhook/new-reservation`, {
        booking_ref: bookingRef,
        ...formData
      }, { headers: { "X-Webhook-Secret": "bdc-webhook-2026-secure" } });
      toast.success("Reservation created!");
      setAddModal(false);
      setFormData({
        customer_name: "",
        phone: "",
        email: "",
        party_size: 2,
        date: format(new Date(), "yyyy-MM-dd"),
        time: "19:00",
        notes: ""
      });
      fetchReservations();
    } catch (error) {
      console.error("Error creating reservation:", error);
      toast.error("Failed to create reservation");
    }
  };

  const handleEditClick = (reservation) => {
    // Just open edit modal directly - staff code will be required on save
    setEditModal({ open: true, reservation: { ...reservation } });
  };

  const handleUpdateReservation = () => {
    // Require staff code before saving changes
    setStaffCodeModal({
      open: true,
      actionType: "Edit",
      actionDescription: `Update reservation for ${editModal.reservation.customer_name}`,
      reservationRef: editModal.reservation.booking_id,
      onConfirm: () => updateReservationConfirmed()
    });
  };

  const updateReservationConfirmed = async () => {
    try {
      await axios.put(`${API}/reservations/${editModal.reservation.booking_id}`, {
        customer_name: editModal.reservation.customer_name,
        phone: editModal.reservation.phone,
        email: editModal.reservation.email,
        party_size: editModal.reservation.party_size,
        time: editModal.reservation.time,
        notes: editModal.reservation.notes
      });
      toast.success("Reservation updated!");
      setEditModal({ open: false, reservation: null });
      fetchReservations();
    } catch (error) {
      console.error("Error updating reservation:", error);
      toast.error("Failed to update reservation");
    }
  };

  // Calculate total covers (Confirmed + Seated)
  const totalCovers = reservations
    .filter(r => ["confirmed", "seated"].includes(r.status))
    .reduce((sum, r) => sum + r.party_size, 0);

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed": return "text-[#22C55E]";
      case "seated": return "text-[#3B82F6]";
      case "cancelled": return "text-[#EF4444]";
      case "no-show": return "text-[#A1A1AA]";
      default: return "";
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#09090B]">
      {/* Header with Total Covers */}
      <div className="border-b border-[#27272A] bg-[#18181B]/80 backdrop-blur-md">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight font-mono">The Host Stand</h1>
              <p className="text-sm text-[#A1A1AA] mt-1">High-density reservation view</p>
            </div>
            <div className="flex items-center gap-3">
              <Popover>
                <PopoverTrigger asChild>
                  <Button data-testid="date-picker-button" variant="outline" className="font-mono bg-[#18181B] border-[#27272A] hover:bg-[#27272A]">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(selectedDate, "PPP")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#18181B] border-[#27272A]">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    initialFocus
                    className="text-[#FAFAFA]"
                  />
                </PopoverContent>
              </Popover>

              <Button 
                data-testid="add-walkin-button"
                onClick={() => setAddModal(true)} 
                className="bg-[#22C55E] hover:bg-[#22C55E]/90 shadow-[0_0_10px_rgba(34,197,94,0.2)]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Walk-in / Add
              </Button>
            </div>
          </div>

          {/* Total Covers Counter */}
          <div className="glass-card p-4 rounded-lg neon-border">
            <div className="flex items-center gap-3">
              <UsersIcon className="w-10 h-10 text-[#22C55E]" />
              <div>
                <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">Total Guests Today</p>
                <p data-testid="total-covers-counter" className="text-4xl font-bold font-mono text-[#22C55E]">{totalCovers}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dense Table View */}
      <div className="flex-1 overflow-auto scroll-container p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#22C55E]"></div>
          </div>
        ) : reservations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 glass-card rounded-lg">
            <CalendarIcon className="w-16 h-16 text-[#27272A] mb-4" />
            <p className="text-lg text-[#A1A1AA]">No reservations for this date</p>
          </div>
        ) : (
          <TimelineView 
            date={selectedDate}
            reservations={reservations}
            onReservationClick={handleEditClick}
            onStatusChange={handleStatusChange}
          />
        )}
      </div>

      {/* Add Walk-in Modal */}
      <Dialog open={addModal} onOpenChange={setAddModal}>
        <DialogContent className="bg-[#18181B] border-[#27272A]">
          <DialogHeader>
            <DialogTitle className="font-mono text-xl">Add Walk-in / Manual Booking</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Create a new reservation manually</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#A1A1AA]">Name *</Label>
                <Input
                  data-testid="add-name-input"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  className="bg-[#09090B] border-[#27272A] text-[#FAFAFA]"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Phone *</Label>
                <Input
                  data-testid="add-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-[#09090B] border-[#27272A] text-[#FAFAFA]"
                />
              </div>
            </div>
            <div>
              <Label className="text-[#A1A1AA]">Email</Label>
              <Input
                data-testid="add-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-[#09090B] border-[#27272A] text-[#FAFAFA]"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-[#A1A1AA]">Date *</Label>
                <Input
                  data-testid="add-date-input"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-[#09090B] border-[#27272A] text-[#FAFAFA]"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Time *</Label>
                <Input
                  data-testid="add-time-input"
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  className="bg-[#09090B] border-[#27272A] text-[#FAFAFA]"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Party Size *</Label>
                <Input
                  data-testid="add-party-size-input"
                  type="number"
                  min="1"
                  value={formData.party_size}
                  onChange={(e) => setFormData({ ...formData, party_size: parseInt(e.target.value) })}
                  className="bg-[#09090B] border-[#27272A] text-[#FAFAFA]"
                />
              </div>
            </div>
            <div>
              <Label className="text-[#A1A1AA]">Notes / Special Requests</Label>
              <Textarea
                data-testid="add-notes-input"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="bg-[#09090B] border-[#27272A] text-[#FAFAFA]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddModal(false)} className="border-[#27272A]">Cancel</Button>
            <Button data-testid="save-reservation-button" onClick={createReservation} className="bg-[#22C55E] hover:bg-[#22C55E]/90">
              Save Reservation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, reservation: null })}>
        <DialogContent className="bg-[#18181B] border-[#27272A]">
          <DialogHeader>
            <DialogTitle className="font-mono text-xl">Edit Reservation</DialogTitle>
          </DialogHeader>
          {editModal.reservation && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#A1A1AA]">Name</Label>
                  <Input
                    value={editModal.reservation.customer_name}
                    onChange={(e) => setEditModal({
                      ...editModal,
                      reservation: { ...editModal.reservation, customer_name: e.target.value }
                    })}
                    className="bg-[#09090B] border-[#27272A] text-[#FAFAFA]"
                  />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Phone</Label>
                  <Input
                    value={editModal.reservation.phone}
                    onChange={(e) => setEditModal({
                      ...editModal,
                      reservation: { ...editModal.reservation, phone: e.target.value }
                    })}
                    className="bg-[#09090B] border-[#27272A] text-[#FAFAFA]"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#A1A1AA]">Time</Label>
                  <Input
                    type="time"
                    value={editModal.reservation.time}
                    onChange={(e) => setEditModal({
                      ...editModal,
                      reservation: { ...editModal.reservation, time: e.target.value }
                    })}
                    className="bg-[#09090B] border-[#27272A] text-[#FAFAFA]"
                  />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Party Size</Label>
                  <Input
                    type="number"
                    min="1"
                    value={editModal.reservation.party_size}
                    onChange={(e) => setEditModal({
                      ...editModal,
                      reservation: { ...editModal.reservation, party_size: parseInt(e.target.value) }
                    })}
                    className="bg-[#09090B] border-[#27272A] text-[#FAFAFA]"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Notes</Label>
                <Textarea
                  value={editModal.reservation.notes || ""}
                  onChange={(e) => setEditModal({
                    ...editModal,
                    reservation: { ...editModal.reservation, notes: e.target.value }
                  })}
                  rows={3}
                  className="bg-[#09090B] border-[#27272A] text-[#FAFAFA]"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, reservation: null })} className="border-[#27272A]">Cancel</Button>
            <Button onClick={handleUpdateReservation} className="bg-[#22C55E] hover:bg-[#22C55E]/90">
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notes Modal */}
      <Dialog open={notesModal.open} onOpenChange={(open) => setNotesModal({ open, notes: "" })}>
        <DialogContent className="bg-[#18181B] border-[#27272A]">
          <DialogHeader>
            <DialogTitle className="font-mono">Reservation Notes</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-[#FAFAFA] whitespace-pre-wrap">{notesModal.notes}</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Staff Code Confirmation Modal */}
      <StaffCodeModal
        open={staffCodeModal.open}
        onClose={() => setStaffCodeModal({ ...staffCodeModal, open: false })}
        actionType={staffCodeModal.actionType}
        actionDescription={staffCodeModal.actionDescription}
        reservationRef={staffCodeModal.reservationRef}
        onConfirm={staffCodeModal.onConfirm}
      />
    </div>
  );
};

export default DashboardNew;
